import { SmallDevice } from "@/components/layout/SmallDevice";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface ScreenSizeContextType {
  isDesktop: boolean;
  isHoverSupported: boolean;
  isWithinResponsiveRange: boolean;
  shouldShowSmallDeviceMessage: boolean;
  shouldShowRotateMessage: boolean;
  shouldShowResizeMessage: boolean;
  isXLBreakpoint: boolean;
}

const ScreenSizeContext = createContext<ScreenSizeContextType | undefined>(
  undefined,
);

interface ScreenSizeProviderProps {
  children: ReactNode;
  minWidth?: number;
  maxWidth?: number;
}

export const ScreenSizeProvider: React.FC<ScreenSizeProviderProps> = ({
  children,
  minWidth = 1024,
  maxWidth = 1280,
}) => {
  const [isDesktop, setIsDesktop] = useState(false);
  const [isWithinResponsiveRange, setIsWithinResponsiveRange] = useState(false);
  const [isHoverSupported, setIsHoverSupported] = useState(false);
  const [shouldShowSmallDeviceMessage, setShouldShowSmallDeviceMessage] =
    useState(false);
  const [shouldShowRotateMessage, setShouldShowRotateMessage] = useState(false);
  const [shouldShowResizeMessage, setShouldShowResizeMessage] = useState(false);
  const [isXLBreakpoint, setIsXLBreakpoint] = useState(false);

  useEffect(() => {
    const checkResponsiveState = () => {
      // Detect device type
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileKeywords = [
        "mobile",
        "android",
        "iphone",
        "ipad",
        "tablet",
        "ipod",
        "opera mini",
      ];
      const isMobileDevice =
        navigator.userAgentData?.mobile ||
        mobileKeywords.some((keyword) => userAgent.includes(keyword));

      setIsDesktop(!isMobileDevice);

      // Get current dimensions
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const isCurrentlyPortrait =
        window.screen.orientation.type === "portrait-primary" ||
        window.screen.orientation.type === "portrait-secondary";
      const supportsHover = window.matchMedia("(hover: hover)").matches;

      // Reset all states
      setShouldShowSmallDeviceMessage(false);
      setShouldShowRotateMessage(false);
      setShouldShowResizeMessage(false);
      setIsHoverSupported(supportsHover);

      setIsXLBreakpoint(screenWidth >= 1280);

      if (!isMobileDevice) {
        // Desktop logic
        const availWidth = window.screen.availWidth;

        if (availWidth < minWidth) {
          // Desktop screen is too small - not supported
          setShouldShowSmallDeviceMessage(true);
        } else if (screenWidth < minWidth) {
          // Desktop has good screen but window is too small - tell them to resize
          setShouldShowResizeMessage(true);
        }
        // If desktop has good screen and good window size, show normal app
      } else {
        // Mobile device logic
        const isWithinResponsiveWidth =
          screenWidth >= minWidth && screenWidth <= maxWidth;
        const isWithinDesktopSize = screenWidth > maxWidth;

        console.debug(
          "ScreenSizeProvider - screenWidth:",
          screenWidth,
          `${screenWidth} >= ${minWidth} && ${screenWidth} <= ${maxWidth}`,
        );

        setIsWithinResponsiveRange(isWithinResponsiveWidth);

        if (isWithinResponsiveWidth || isWithinDesktopSize) {
          // Screen dimensions are good - show normal app
          return;
        } else if (screenHeight >= minWidth && isCurrentlyPortrait) {
          // Height is good but width is too small, and device is in portrait - show rotate message
          setShouldShowRotateMessage(true);
        } else {
          // Device screen is too small overall - not supported
          setShouldShowSmallDeviceMessage(true);
        }
      }
    };

    // Initial check
    checkResponsiveState();

    // Set up ResizeObserver to monitor viewport changes
    const resizeObserver = new ResizeObserver(() => {
      checkResponsiveState();
    });

    // Observe the document element for size changes
    resizeObserver.observe(document.documentElement);

    // Listen for orientation changes (still needed for mobile devices)
    const handleOrientationChange = () => setTimeout(checkResponsiveState, 100);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [minWidth, maxWidth]);

  // If any error state is active, show SmallDevice component
  if (
    shouldShowSmallDeviceMessage ||
    shouldShowRotateMessage ||
    shouldShowResizeMessage
  ) {
    return (
      <SmallDevice
        isDesktop={isDesktop}
        shouldShowRotateMessage={shouldShowRotateMessage}
        shouldShowResizeMessage={shouldShowResizeMessage}
      />
    );
  }

  return (
    <ScreenSizeContext.Provider
      value={{
        isDesktop,
        isHoverSupported,
        isWithinResponsiveRange,
        shouldShowSmallDeviceMessage,
        shouldShowRotateMessage,
        shouldShowResizeMessage,
        isXLBreakpoint,
      }}
    >
      {children}
    </ScreenSizeContext.Provider>
  );
};

// Hook export
export const useScreenSize = (): ScreenSizeContextType => {
  const context = useContext(ScreenSizeContext);
  if (!context) {
    throw new Error("useScreenSize must be used within a ScreenSizeProvider");
  }
  return context;
};
